export type BillingIntent = {
  plan: "free" | "plus" | "pro" | "businessPlus" | "businessPro";
  currency: "EUR" | "CZK";
  interval: "month" | "year";
};

export const billingIntentStorageKey = "takeres.billingIntent";
const intentLifetimeMs = 60 * 60 * 1000;

export function parseBillingIntent(value: unknown): BillingIntent | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const { plan, currency, interval } = candidate;
  if (plan !== "free" && plan !== "plus" && plan !== "pro" && plan !== "businessPlus" && plan !== "businessPro") return null;
  if (currency !== "EUR" && currency !== "CZK") return null;
  if (interval !== "month" && interval !== "year") return null;
  return { plan, currency, interval };
}

export function billingIntentFromSearch(search: string): BillingIntent | null {
  const params = new URLSearchParams(search);
  const period = params.get("billingPeriod");
  return parseBillingIntent({
    plan: params.get("billingPlan"),
    currency: params.get("billingCurrency"),
    interval: period === "monthly" ? "month" : period === "yearly" ? "year" : null,
  });
}

export function readStoredBillingIntent(raw: string | null, now = Date.now()): BillingIntent | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    const intent = parseBillingIntent(value);
    if (!intent) return null;
    const expiresAt = (value as Record<string, unknown>).expiresAt;
    return typeof expiresAt === "number" && expiresAt > now && expiresAt <= now + intentLifetimeMs
      ? intent
      : null;
  } catch {
    return null;
  }
}

export function rememberBillingIntent(): void {
  if (typeof window === "undefined") return;
  const intent = billingIntentFromSearch(window.location.search);
  if (!intent) return;
  try {
    window.sessionStorage.setItem(billingIntentStorageKey, JSON.stringify({
      ...intent,
      expiresAt: Date.now() + intentLifetimeMs,
    }));
  } catch {
    // The URL still supplies the selection when browser storage is unavailable.
  }
}

export function currentBillingIntent(): BillingIntent | null {
  if (typeof window === "undefined") return null;
  const intent = billingIntentFromSearch(window.location.search);
  if (intent) return intent;
  try {
    return readStoredBillingIntent(window.sessionStorage.getItem(billingIntentStorageKey));
  } catch {
    return null;
  }
}

export function consumeBillingIntent(): BillingIntent | null {
  const intent = currentBillingIntent();
  if (typeof window === "undefined") return intent;
  try {
    window.sessionStorage.removeItem(billingIntentStorageKey);
  } catch {
    // Storage can be disabled independently of URL access.
  }
  const url = new URL(window.location.href);
  for (const key of ["billingPlan", "billingCurrency", "billingPeriod"]) url.searchParams.delete(key);
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url.href);
  return intent;
}

export function billingIntentDestination(pathname: string, intent: BillingIntent | null): string | null {
  const match = /^\/c\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/iu.exec(pathname);
  if (!intent || !match) return null;
  const params = new URLSearchParams({
    billingPlan: intent.plan,
    billingCurrency: intent.currency,
    billingPeriod: intent.interval === "year" ? "yearly" : "monthly",
  });
  return `/c/${match[1]}/settings/subscription?${params}`;
}
